import type { EncodingId } from '../data/pricing';
import type { TokenizationResult, WorkerRequest, WorkerResponse } from './types';
// `?worker` makes Vite bundle the worker as a self-contained classic script in
// BOTH dev and production. Avoids ES-module-worker compatibility issues
// (notably Safari's stricter handling) by removing the `type: 'module'` path
// entirely. Vite handles the worker.ts source's ES imports by bundling them
// inline into the emitted classic script.
import TokenizerWorker from './worker.ts?worker';

let worker: Worker | null = null;
let nextId = 1;

interface Pending {
  resolve: (r: TokenizationResult) => void;
  reject: (e: Error) => void;
}
const pending = new Map<number, Pending>();

function rejectAllPending(reason: string): void {
  const err = new Error(reason);
  for (const cb of pending.values()) cb.reject(err);
  pending.clear();
}

function getWorker(): Worker {
  if (!worker) {
    worker = new TokenizerWorker();
    worker.addEventListener('message', (e: MessageEvent<WorkerResponse>) => {
      const cb = pending.get(e.data.id);
      if (!cb) return;
      pending.delete(e.data.id);
      if ('error' in e.data) cb.reject(new Error(e.data.error));
      else cb.resolve(e.data.result);
    });
    worker.addEventListener('error', (e: ErrorEvent) => {
      rejectAllPending(`tokenizer worker crashed: ${e.message}`);
      worker = null;
    });
    worker.addEventListener('messageerror', () => {
      rejectAllPending('tokenizer worker messageerror');
      worker = null;
    });
  }
  return worker;
}

export function tokenize(text: string, encoding: EncodingId): Promise<TokenizationResult> {
  return new Promise((resolve, reject) => {
    const id = nextId++;
    pending.set(id, { resolve, reject });
    const req: WorkerRequest = { id, text, encoding };
    getWorker().postMessage(req);
  });
}

export function tokenizeAll(text: string): Promise<Record<EncodingId, TokenizationResult>> {
  return Promise.all([
    tokenize(text, 'p50k_base'),
    tokenize(text, 'cl100k_base'),
    tokenize(text, 'o200k_base')
  ]).then(([p, c, o]) => ({ p50k_base: p, cl100k_base: c, o200k_base: o }));
}
